/**
 * CORE-896 — согласие на передачу данных стороннему ИИ-сервису.
 *
 * App Store Guidelines 5.1.1(i) / 5.1.2(i): прежде чем отправить персональные
 * данные стороннему ИИ-сервису, приложение обязано сказать, ЧТО отправляет и
 * КОМУ, и получить разрешение. Apple отдельно оговаривает, что упоминания в
 * Политике конфиденциальности недостаточно — нужна точка согласия в интерфейсе.
 *
 * Состояние живёт локально (`storage`), но подписчики обновляются мгновенно:
 * согласие отзывают в «Настройках», а гейт висит в открытом чате — без общей
 * шины отзыв доехал бы до чата только после перемонтирования.
 *
 * CORE-1576 — источник правды переехал на сервер. Раньше согласие жило ТОЛЬКО
 * здесь, в localStorage, и сервер о нём не знал: нажатие «Принимаю» не
 * создавало никакой записи. После включения серверной проверки это означало бы,
 * что память выключена у всех навсегда — включить её нечем.
 *
 * Поэтому `accept` и `revoke` ходят на сервер, а локальное хранилище осталось
 * кэшем: оно нужно, чтобы гейт не мигал на каждом открытии чата, но решает не
 * оно. Серверное «нет» перекрывает локальное «да» — иначе человек разговаривал
 * бы, считая, что память работает, а она молча выключена.
 *
 * Кэш при этом работает НЕСИММЕТРИЧНО, и это главное в нём. Локальное «нет»
 * показывается сразу — гейт безопасная сторона. Локальное «да» до ответа
 * сервера чат не открывает: кэш мог остаться с тех пор, когда согласие жило
 * только на устройстве, и открыть по нему значило бы показать человеку как
 * согласованное то, на что согласия нет.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../services/api';
import { storage } from '../utils/storage';
import { acceptedDocument, currentOperator } from '../legal/accepted';
import { aiConsentVariant, currentEdition, type AcceptedDocument, type LegalVariant } from '../legal/editions';
import { getCurrentLanguage } from '../i18n';

/**
 * CORE-1576 — версия текста, под которой человек согласился.
 *
 * Это текст самого экрана (префикс `inapp-`), а не документа. С CORE-1401
 * (возврат QA 23.10) у экрана есть документ — «Уведомление об ИИ», — и его
 * редакция пишется в согласие отдельной строкой рядом с текстом экрана.
 *
 * Заново при смене версии согласие НЕ спрашивают: клиент смотрит только, есть
 * ли действующее `ai_data`, а не под какой оно версией. Прежний комментарий
 * здесь обещал обратное. Требовать ли действующую редакцию — решение продукта
 * (замер и последствия — в CORE-1401), и делать его надо с двух сторон сразу:
 * сервер, который перестал засчитывать старую редакцию, без клиента, который
 * спросит снова, молча выключил бы память всем.
 */
// CORE-1567 — без названия получателя (OpenAI) в российском контуре.
export const AI_DATA_CONSENT_VERSION = 'inapp-2026-10-30';

/**
 * Что называет запись согласия на данные ИИ: текст экрана в варианте, который
 * человек читал, и Уведомление об ИИ (вариант у него один — русский).
 */
export function aiDataConsentDocuments(variant: LegalVariant): AcceptedDocument[] {
  return [acceptedDocument('ai-data-consent', variant), currentEdition('ai-notice', 'ru')];
}

export type AiConsentStatus = 'loading' | 'granted' | 'missing';

/** userId → granted. Заполняется по мере чтения; отзыв кладёт `false`. */
const cache = new Map<string, boolean>();
const subscribers = new Set<(userId: string, granted: boolean) => void>();

function publish(userId: string, granted: boolean): void {
  cache.set(userId, granted);
  subscribers.forEach((cb) => cb(userId, granted));
}

/** Разовое чтение вне React (например, перед фоновой отправкой). */
export async function hasAiDataConsent(userId: string): Promise<boolean> {
  if (!userId) return false;
  const cached = cache.get(userId);
  if (cached !== undefined) return cached;
  const granted = await storage.getAiDataConsent(userId);
  cache.set(userId, granted);
  return granted;
}

/**
 * Согласие по данным сервера. Падение сети не считается отказом: иначе перебой
 * связи выглядел бы как отзыв и показывал гейт человеку, который давно
 * согласился. Поэтому при ошибке остаётся локальное значение.
 */
async function serverConsent(userId: string): Promise<boolean | null> {
  try {
    const types = await api.getActiveConsentTypes();
    return types.includes('ai_data');
  } catch {
    return null;
  }
}

export interface UseAiDataConsent {
  status: AiConsentStatus;
  accept: (lang?: 'ru' | 'en') => Promise<void>;
  /** Отзыв согласия — и отказ на самом гейте, и «Отозвать» в настройках. */
  revoke: () => Promise<void>;
}

export function useAiDataConsent(userId: string): UseAiDataConsent {
  const [status, setStatus] = useState<AiConsentStatus>(() => {
    const cached = userId ? cache.get(userId) : undefined;
    if (cached === undefined) return 'loading';
    return cached ? 'granted' : 'missing';
  });

  useEffect(() => {
    let alive = true;

    const onChange = (changedUserId: string, granted: boolean) => {
      if (changedUserId === userId) setStatus(granted ? 'granted' : 'missing');
    };
    subscribers.add(onChange);

    if (!userId) {
      // Некому давать согласие — гейт держит закрытым, но не «loading»,
      // иначе чат навсегда останется спиннером.
      setStatus('missing');
      return () => { subscribers.delete(onChange); };
    }

    hasAiDataConsent(userId).then(async (localGranted) => {
      if (!alive) return;
      // CORE-1401, замечание ревью 02.10 — что делать с теми, кто согласился
      // ДО того, как согласие стало серверным.
      //
      // Их спрашивают заново, и переносить локальное «да» на сервер нельзя.
      // Запись согласия обязана быть доказуемой: версия текста, время,
      // источник. У локального значения нет ничего из этого — оно было
      // поставлено под другим текстом, когда версии ещё не существовало, и
      // записать его сейчас значило бы проставить время и версию, которых
      // человек не видел. Это не перенос согласия, а его подделка.
      //
      // Отсюда и несимметричность ниже. Локальное «нет» показывается сразу:
      // гейт — безопасная сторона, и ошибиться в неё нечем. Локальное «да»
      // до ответа сервера НЕ открывает чат: иначе человек с протухшим кэшем
      // успевал бы написать в окно, которое мы ему показали как согласованное.
      if (!localGranted) setStatus('missing');

      const remote = await serverConsent(userId);
      if (!alive) return;
      if (remote === null) {
        // Сеть не ответила. Перебой — не отказ: остаётся локальное значение,
        // иначе один упавший запрос показал бы гейт тому, кто давно согласился.
        // Безопасно: сама память всё равно за серверными воротами (CORE-1576),
        // то есть в этом окне ничего не запоминается.
        setStatus(localGranted ? 'granted' : 'missing');
        return;
      }
      if (remote !== localGranted) {
        await storage.setAiDataConsent(userId, remote);
        publish(userId, remote);
      }
      setStatus(remote ? 'granted' : 'missing');
    });

    return () => {
      alive = false;
      subscribers.delete(onChange);
    };
  }, [userId]);

  /** `lang` — на каком языке человек читал экран согласия (у него свой переключатель RU/EN). */
  const accept = useCallback(async (lang?: 'ru' | 'en') => {
    if (!userId) return;
    // CORE-1661 — вариант редакции: язык экрана и оператор, которого он назвал.
    const variant = aiConsentVariant(lang ?? getCurrentLanguage(), currentOperator());
    // Сначала сервер. Если запись не создалась, локальное «да» было бы ложью:
    // гейт пропустил бы человека в чат, а память осталась бы выключенной — и
    // снаружи это неотличимо от «модель ничего не запоминает».
    await api.grantConsent({
      type: 'ai_data',
      documentVersion: AI_DATA_CONSENT_VERSION,
      // CORE-1660 — редакция текста экрана из реестра: вариант и SHA-256.
      // CORE-1401 — и редакция Уведомления об ИИ, на которое экран ссылается
      // как на полный текст: доказывать придётся и то, что было в нём.
      documents: aiDataConsentDocuments(variant),
      source: 'ai_chat_gate',
    });
    await storage.setAiDataConsent(userId, true);
    publish(userId, true);
  }, [userId]);

  const revoke = useCallback(async () => {
    if (!userId) return;
    // Отзыв на сервере запускает и уничтожение накопленного (CORE-1578).
    // Поэтому он тоже первый: локальный отзыв без серверного оставил бы данные
    // лежать, а человеку показал бы, что он их отозвал.
    await api.revokeConsent('ai_data');
    await storage.setAiDataConsent(userId, false);
    publish(userId, false);
  }, [userId]);

  return { status, accept, revoke };
}
